import { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, ArrowDownWideNarrow, ArrowUpNarrowWide } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  Download,
  Lock,
  Unlock,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Info,
} from 'lucide-react';
import { barrattAPI, type DivisionPerformanceScore, type QuarterInfo } from '../../lib/api/barratt';
import { suppliersAPI } from '../../lib/api';
import { fetchAllPages, type Company } from '../../lib/api';
import { toast } from 'sonner';

// Score display mapping: 1-5 internal -> 20/40/60/80/100 display
const SCORE_DISPLAY = {
  1: { value: 20, label: 'Poor', color: 'bg-red-500' },
  2: { value: 40, label: 'Below Average', color: 'bg-orange-500' },
  3: { value: 60, label: 'Average', color: 'bg-yellow-500' },
  4: { value: 80, label: 'Good', color: 'bg-lime-500' },
  5: { value: 100, label: 'Excellent', color: 'bg-green-500' },
};

interface Supplier {
  id: number;
  name: string;
}

interface SupplierScore {
  supplier: Supplier;
  score?: DivisionPerformanceScore;
}

const PAGE_SIZES = [100, 250, 500] as const;
type SortKey = 'supplier' | 'score' | 'comments';
type SortDir = 'asc' | 'desc';

export default function DivisionPerformance() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quarters, setQuarters] = useState<QuarterInfo[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [selectedQuarter, setSelectedQuarter] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [supplierScores, setSupplierScores] = useState<SupplierScore[]>([]);
  const [isQuarterCompleted, setIsQuarterCompleted] = useState(false);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const [completedBy, setCompletedBy] = useState<string | null>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<SupplierScore | null>(null);
  const [editScore, setEditScore] = useState<number>(5);
  const [editComments, setEditComments] = useState<string>('');
  // List controls: search, sort (worst first by default — unscored count as worst), paging.
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [pageSize, setPageSize] = useState<number>(100);
  const [page, setPage] = useState(1);

  const orderedRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term ? supplierScores.filter((ss) => ss.supplier.name.toLowerCase().includes(term)) : supplierScores;
    const value = (ss: SupplierScore): number | string =>
      sortKey === 'supplier' ? ss.supplier.name.toLowerCase()
        : sortKey === 'comments' ? (ss.score?.comments || '').toLowerCase()
        : (ss.score?.score ?? 0);
    const sign = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = value(a); const vb = value(b);
      if (va < vb) return -1 * sign;
      if (va > vb) return 1 * sign;
      return a.supplier.name.localeCompare(b.supplier.name);
    });
  }, [supplierScores, search, sortKey, sortDir]);
  const pageCount = Math.max(1, Math.ceil(orderedRows.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageRows = orderedRows.slice((safePage - 1) * pageSize, safePage * pageSize);
  useEffect(() => { setPage(1); }, [search, sortKey, sortDir, pageSize, selectedQuarter, selectedYear]);

  const sortBy = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sortIcon = (key: SortKey) =>
    sortKey !== key ? <ArrowUpDown className="h-3 w-3 ml-1 inline text-muted-foreground" />
      : sortDir === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 inline" /> : <ArrowDown className="h-3 w-3 ml-1 inline" />;

  const pager = (position: 'top' | 'bottom') => (
    <div className="flex flex-wrap items-center justify-between gap-3" data-testid={`perf-pager-${position}`}>
      <span className="text-sm text-muted-foreground">
        {orderedRows.length === 0 ? 'No suppliers match'
          : `Showing ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, orderedRows.length)} of ${orderedRows.length}`}
      </span>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Show</span>
        <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
          <SelectTrigger className="w-20" aria-label="Rows per page"><SelectValue /></SelectTrigger>
          <SelectContent>{PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => setPage(safePage - 1)} disabled={safePage <= 1} aria-label="Previous page"><ChevronLeft className="h-4 w-4" /></Button>
        <span className="text-sm tabular-nums">Page {safePage} of {pageCount}</span>
        <Button variant="outline" size="sm" onClick={() => setPage(safePage + 1)} disabled={safePage >= pageCount} aria-label="Next page"><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);

        // Load quarters, years, and current quarter in parallel
        const [quartersRes, yearsRes, currentRes] = await Promise.all([
          barrattAPI.getDivisionPerformanceQuarters(),
          barrattAPI.getDivisionPerformanceYears(),
          barrattAPI.getCurrentQuarter(),
        ]);

        setQuarters(quartersRes.data.quarters);
        setYears(yearsRes.data.years);
        setSelectedQuarter(currentRes.data.quarter);
        setSelectedYear(currentRes.data.year);
      } catch (error) {
        console.error('Failed to load initial data:', error);
        toast.error('Failed to load quarter information');
        // Set defaults
        setQuarters([
          { value: 'Q1', label: 'Q1 (Apr-Jun)' },
          { value: 'Q2', label: 'Q2 (Jul-Sep)' },
          { value: 'Q3', label: 'Q3 (Oct-Dec)' },
          { value: 'Q4', label: 'Q4 (Jan-Mar)' },
        ]);
        setYears([new Date().getFullYear()]);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Load scores when quarter/year changes
  const loadScores = useCallback(async () => {
    if (!selectedQuarter || !selectedYear) return;

    try {
      setLoading(true);

      // Load quarter status, scores, and suppliers in parallel. Suppliers
      // walk pages — the backend clamps page_size to 100, so a single
      // request left suppliers past the cap off the scorecard entirely.
      // Suppliers come from the profiles endpoint (approved panel), which a
      // customer admin can read — /core/companies/ is Helix-operator only
      // and 403'd this page for every real Barratt user.
      const [statusRes, scoresRes, profiles] = await Promise.all([
        barrattAPI.getQuarterStatus(selectedQuarter, selectedYear),
        barrattAPI.getDivisionPerformanceScores({
          quarter: selectedQuarter,
          year: selectedYear,
        }),
        fetchAllPages<{ company_id: number; company_name: string }>((page) =>
          suppliersAPI.getProfiles({
            minimal: true, is_approved: true, page_size: 100, page,
          })),
      ]);
      const suppliers: Pick<Company, 'id' | 'name'>[] = profiles.map((p) => ({
        id: p.company_id,
        name: p.company_name,
      }));

      setIsQuarterCompleted(statusRes.data.is_completed);
      setCompletedAt(statusRes.data.completed_at || null);
      setCompletedBy(statusRes.data.completed_by || null);

      // Map suppliers with their scores
      const scores = scoresRes.data.results || [];

      const supplierScoreMap = new Map(
        scores.map((s) => [s.supplier, s])
      );

      const mappedScores: SupplierScore[] = suppliers.map((supplier) => ({
        supplier: { id: supplier.id, name: supplier.name },
        score: supplierScoreMap.get(supplier.id),
      }));

      // Sort by supplier name
      mappedScores.sort((a, b) => a.supplier.name.localeCompare(b.supplier.name));
      setSupplierScores(mappedScores);
    } catch (error) {
      console.error('Failed to load scores:', error);
      toast.error('Failed to load performance scores');
    } finally {
      setLoading(false);
    }
  }, [selectedQuarter, selectedYear]);

  useEffect(() => {
    loadScores();
  }, [loadScores]);

  const openEditDialog = (supplierScore: SupplierScore) => {
    if (isQuarterCompleted) {
      toast.error('This quarter is completed and cannot be modified');
      return;
    }
    setEditingSupplier(supplierScore);
    setEditScore(supplierScore.score?.score || 5);
    setEditComments(supplierScore.score?.comments || '');
    setEditDialogOpen(true);
  };

  const handleSaveScore = async () => {
    if (!editingSupplier) return;

    // Validate comments for low scores
    if (editScore <= 3 && !editComments.trim()) {
      toast.error('Comments are required for scores of 1, 2, or 3');
      return;
    }

    try {
      setSaving(true);
      await barrattAPI.saveDivisionPerformanceScore({
        supplier_id: editingSupplier.supplier.id,
        score: editScore,
        comments: editComments,
        quarter: selectedQuarter,
        year: selectedYear,
      });

      toast.success('Score saved successfully');
      setEditDialogOpen(false);
      loadScores();
    } catch (error) {
      console.error('Failed to save score:', error);
      toast.error('Failed to save score');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteQuarter = async () => {
    try {
      setSaving(true);
      await barrattAPI.completeQuarter(selectedQuarter, selectedYear);
      toast.success('Quarter marked as completed');
      loadScores();
    } catch (error) {
      console.error('Failed to complete quarter:', error);
      toast.error('Failed to complete quarter');
    } finally {
      setSaving(false);
    }
  };

  const handleReopenQuarter = async () => {
    try {
      setSaving(true);
      await barrattAPI.reopenQuarter(selectedQuarter, selectedYear);
      toast.success('Quarter reopened for editing');
      loadScores();
    } catch (error) {
      console.error('Failed to reopen quarter:', error);
      toast.error('Failed to reopen quarter');
    } finally {
      setSaving(false);
    }
  };

  const getScoreBadge = (score: number | undefined) => {
    if (!score) return <Badge variant="outline">Not Scored</Badge>;
    const display = SCORE_DISPLAY[score as keyof typeof SCORE_DISPLAY];
    return (
      <Badge className={`${display.color} text-white`}>
        {display.value} - {display.label}
      </Badge>
    );
  };

  /** Excel of the list as shown after search and sort — every page. */
  const handleExport = () => {
    try {
      const header = ['Supplier', 'Score', 'Score Label', 'Comments'];
      const body = orderedRows.map((ss) => [
        ss.supplier.name,
        ss.score?.score ? SCORE_DISPLAY[ss.score.score as keyof typeof SCORE_DISPLAY].value : '',
        ss.score?.score ? SCORE_DISPLAY[ss.score.score as keyof typeof SCORE_DISPLAY].label : 'Not Scored',
        ss.score?.comments || '',
      ]);
      const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
      sheet['!cols'] = [{ wch: 44 }, { wch: 8 }, { wch: 14 }, { wch: 60 }];
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, `${selectedQuarter} ${selectedYear}`);
      const suffix = search.trim() ? `_${search.trim().replace(/[^a-z0-9]+/gi, '_')}` : '';
      XLSX.writeFile(book, `Division_Performance_${selectedQuarter}_${selectedYear}${suffix}.xlsx`);
      toast.success('Export complete');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Division Performance</h1>
          <p className="text-muted-foreground">
            Quarterly supplier performance scoring with Barratt quarter mapping
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4 mr-2" />
          Export Excel
        </Button>
      </div>

      {/* Quarter Info Card */}
      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Barratt Quarter Mapping</p>
              <p className="text-muted-foreground">
                Q1: Apr-Jun | Q2: Jul-Sep | Q3: Oct-Dec | Q4: Jan-Mar
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters & Status */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4 justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Quarter:</label>
                <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Select quarter" />
                  </SelectTrigger>
                  <SelectContent>
                    {quarters.map((q) => (
                      <SelectItem key={q.value} value={q.value}>
                        {q.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Year:</label>
                <Select
                  value={selectedYear.toString()}
                  onValueChange={(v) => setSelectedYear(parseInt(v))}
                >
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Quarter Status & Actions */}
            <div className="flex items-center gap-4">
              {isQuarterCompleted ? (
                <>
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>
                      Completed{completedBy ? ` by ${completedBy}` : ''}
                      {completedAt ? ` on ${new Date(completedAt).toLocaleDateString()}` : ''}
                    </span>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" disabled={saving}>
                        <Unlock className="h-4 w-4 mr-2" />
                        Reopen Quarter
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reopen Quarter?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will allow modifications to scores for {selectedQuarter} {selectedYear}.
                          Are you sure you want to reopen this quarter?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleReopenQuarter}>Reopen</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="default" size="sm" disabled={saving}>
                      <Lock className="h-4 w-4 mr-2" />
                      Complete Quarter
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Complete Quarter?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will lock all scores for {selectedQuarter} {selectedYear}.
                        No further modifications will be allowed until reopened.
                        Are you sure you want to complete this quarter?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleCompleteQuarter}>Complete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Supplier Scores - {selectedQuarter} {selectedYear}
          </CardTitle>
          <CardDescription>
            Click on a supplier row to edit their score. Scores: 1 (Poor/20) to 5 (Excellent/100).
            Comments are required for scores 1-3.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative w-72 max-w-full">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search supplier…"
                       className="pl-8" aria-label="Search supplier" data-testid="perf-search" />
              </div>
              <Button variant="outline" size="sm" data-testid="perf-sort"
                      onClick={() => { setSortKey('score'); setSortDir(sortKey === 'score' && sortDir === 'asc' ? 'desc' : 'asc'); }}>
                {sortKey === 'score' && sortDir === 'desc'
                  ? <><ArrowUpNarrowWide className="h-4 w-4 mr-2" />Best first</>
                  : <><ArrowDownWideNarrow className="h-4 w-4 mr-2" />Worst first</>}
              </Button>
            </div>
            {pager('top')}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%] cursor-pointer select-none" onClick={() => sortBy('supplier')} data-testid="perf-sort-supplier">Supplier{sortIcon('supplier')}</TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => sortBy('score')}>Score{sortIcon('score')}</TableHead>
                  <TableHead className="w-[35%] cursor-pointer select-none" onClick={() => sortBy('comments')}>Comments{sortIcon('comments')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderedRows.length === 0 ? (
                  <TableRow>
                    <TableCell className="text-muted-foreground" colSpan={3}>
                      No suppliers match.
                    </TableCell>
                  </TableRow>
                ) : (
                  pageRows.map((ss) => (
                    <TableRow
                      key={ss.supplier.id}
                      className={`cursor-pointer hover:bg-muted/50 ${
                        isQuarterCompleted ? 'cursor-not-allowed opacity-75' : ''
                      }`}
                      onClick={() => openEditDialog(ss)}
                    >
                      <TableCell className="font-medium">{ss.supplier.name}</TableCell>
                      <TableCell>{getScoreBadge(ss.score?.score)}</TableCell>
                      <TableCell className="text-muted-foreground whitespace-pre-wrap break-words max-w-[480px] align-top">
                        {ss.score?.comments || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {pager('bottom')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Score Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Score Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {Object.entries(SCORE_DISPLAY).map(([score, display]) => (
              <div key={score} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded ${display.color}`} />
                <span className="text-sm">
                  {score} = {display.value} ({display.label})
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit Score Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Score - {editingSupplier?.supplier.name}</DialogTitle>
            <DialogDescription>
              Set the performance score for {selectedQuarter} {selectedYear}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Performance Score</label>
              <Select
                value={editScore.toString()}
                onValueChange={(v) => setEditScore(parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 4, 3, 2, 1].map((score) => {
                    const display = SCORE_DISPLAY[score as keyof typeof SCORE_DISPLAY];
                    return (
                      <SelectItem key={score} value={score.toString()}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded ${display.color}`} />
                          {score} - {display.value} ({display.label})
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Comments
                {editScore <= 3 && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              <Textarea
                placeholder={
                  editScore <= 3
                    ? 'Comments are required for scores 1-3. Explain the reason for the low score.'
                    : 'Optional comments about the supplier performance'
                }
                value={editComments}
                onChange={(e) => setEditComments(e.target.value)}
                rows={4}
                maxLength={500}
              />
              {editScore <= 3 && !editComments.trim() && (
                <div className="flex items-center gap-2 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4" />
                  Comments are required for scores of 1, 2, or 3
                </div>
              )}
              <div className="text-xs text-muted-foreground text-right">
                {editComments.length}/500 characters
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveScore}
              disabled={saving || (editScore <= 3 && !editComments.trim())}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Score
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
